import { ReactNode, useCallback, useEffect, useRef } from 'react';

import { useDrawingShapeContext } from '../drawingShapeContext';
import { useUndoStack } from './hooks';
import { UndoStackContext } from './undoStack.context';
import { DoOperation, UndoStackContextType } from './undoStack.types';
import { addToStack, moveIndex } from './undoStack.utils';

import { useSelectedEdge } from '~/api/easy-estimator-api/edges-api/hooks/useSelectedEdge';
import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import { updateApplianceCoords } from '~/core/helpers';
import useAppliance from '~/core/hooks/useAppliance';
import { validateApplianceConsistency } from '~/core/utils/drawingTool/drawingTool.utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectSelectedColorId,
  updateSelectedColorId,
} from '~/redux/slices/color';
import {
  selectSelectedAppliance,
  setCookingAppliance,
} from '~/redux/slices/cookingAppliance';
import {
  selectApplianceCutouts,
  selectCustomCutouts,
  setApplianceCutouts,
  setCustomCutouts,
} from '~/redux/slices/cutout';
import {
  selectActiveDrawingObject,
  setActiveDrawingObject,
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { updateUserSelectedEdge } from '~/redux/slices/edge';
import { selectIsNewEstimate } from '~/redux/slices/estimate';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import {
  clearUndoStackState,
  pushOperationUndoStack,
  selectDrawingShapeElementsStack,
  selectMaxUndoIndex,
  selectUndoIndex,
  updateLatestPushedUserDesignOperation,
  updateUndoIndex,
} from '~/redux/slices/undoStack';

export const UndoStackContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const dispatch = useAppDispatch();

  const {
    shapesStack,
    setShapesStack,
    lastUserSelectedDesignOperation,
    isLatestOperationAnUndoRedoAction,
    isAddingAnApplianceTheLastOperationBeforeUndoRedo,
    latestPushedUserDesignOperation,
  } = useUndoStack();

  const undoIndex = useAppSelector(selectUndoIndex);
  const maxUndoIndex = useAppSelector(selectMaxUndoIndex);
  const { selectedValue: selectedEdge } = useSelectedEdge();
  const selectedColorId = useAppSelector(selectSelectedColorId);
  const drawingShapeElementsStack = useAppSelector(
    selectDrawingShapeElementsStack
  );

  const currentActiveDrawingObject = useAppSelector(selectActiveDrawingObject);
  const selectedRanges = useAppSelector(
    selectSelectedAppliance('selectedRangeApplianceProduct')
  );
  const selectedCooktops = useAppSelector(
    selectSelectedAppliance('selectedCooktopApplianceProduct')
  );
  const selectedCustomCutouts = useAppSelector(selectCustomCutouts);
  const selectedAppliancesCutouts = useAppSelector(selectApplianceCutouts);
  const canvas = useAppSelector(selectFabricCanvas);
  const isNewEstimate = useAppSelector(selectIsNewEstimate);

  const {
    getters: { areThereShapesInTheDrawingArea, drawingShapes },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { removeApplianceFromDrawingArea } = useAppliance();

  const pushToUndoStack = useCallback(() => {
    const shapes = drawingTool.cloneShapes(drawingShapes);

    if (
      !areThereShapesInTheDrawingArea ||
      !selectedEdge ||
      !selectedColorId ||
      shapes.length <= 1
    ) {
      return;
    }

    const index = moveIndex('fwd', undoIndex) as number;
    const updateStack = addToStack(index);

    dispatch(updateUndoIndex(index));
    setShapesStack(updateStack(shapesStack, shapes));
    dispatch(
      pushOperationUndoStack({
        edge: selectedEdge,
        colorId: selectedColorId,
        activeDrawingObject: currentActiveDrawingObject,
        selectedCooktops,
        selectedRanges,
        selectedCustomCutouts,
        selectedAppliancesCutouts,
      })
    );
  }, [
    currentActiveDrawingObject,
    areThereShapesInTheDrawingArea,
    dispatch,
    drawingShapes,
    selectedAppliancesCutouts,
    selectedColorId,
    selectedCooktops,
    selectedCustomCutouts,
    selectedEdge,
    selectedRanges,
    setShapesStack,
    shapesStack,
    undoIndex,
  ]);

  const pullFromUndoStack = useCallback(
    (operation: DoOperation) => {
      const index = moveIndex(operation === 'undo' ? 'bwd' : 'fwd', undoIndex);

      if (index === undefined) {
        return;
      }
      const {
        activeDrawingObject,
        edge,
        colorId,
        selectedAppliancesCutouts,
        selectedCustomCutouts,
        selectedCooktops,
        selectedRanges,
      } = drawingShapeElementsStack[index];

      const shapes = drawingTool
        .cloneShapes(shapesStack[index])
        .filter((shape) => !!shape);

      // TODO(SD-1709): dead branch. setActiveDrawingObject (drawingShape.slice.ts)
      // only ever forwards an existing activeDrawingObject.previousObject; it never
      // captures the outgoing active object as the new previousObject. No normal
      // dispatch sequence can populate previousObject, so this condition can never
      // be true through real app usage. Needs a decision from the undo/appliance
      // owner: either make setActiveDrawingObject capture the outgoing object (a
      // behavior change, add regression test), or remove this branch as obsolete.
      if (
        operation === 'undo' &&
        isAddingAnApplianceTheLastOperationBeforeUndoRedo &&
        currentActiveDrawingObject?.previousObject?.object?.type === 'appliance'
      ) {
        removeApplianceFromDrawingArea(
          activeDrawingObject?.previousObject?.object?.id
        );
      }

      dispatch(updateUndoIndex(index));
      dispatch(setActiveDrawingObject(activeDrawingObject?.object));
      dispatch(updateUserSelectedEdge(edge));
      dispatch(updateSelectedColorId(colorId));
      dispatch(setApplianceCutouts(selectedAppliancesCutouts));
      dispatch(setCustomCutouts(selectedCustomCutouts));
      dispatch(
        setCookingAppliance({
          selectedType: 'selectedCooktopApplianceProduct',
          selectedAppliances: selectedCooktops,
        })
      );
      dispatch(
        setCookingAppliance({
          selectedType: 'selectedRangeApplianceProduct',
          selectedAppliances: selectedRanges,
        })
      );

      updateDrawingShapes(shapes);

      shapes
        .filter((shape) => shape.type !== ShapeType.FAUX_SHAPE)
        .forEach((shape) =>
          shape.appliances.forEach((appliance) =>
            updateApplianceCoords(appliance, canvas)
          )
        );

      // Clean up any orphaned canvas appliances that don't exist in the new data model
      // This is critical after undo/redo because shapes are cloned with new references
      validateApplianceConsistency(shapes, canvas);

      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      canvas,
      currentActiveDrawingObject?.previousObject?.object,
      dispatch,
      drawingShapeElementsStack,
      isAddingAnApplianceTheLastOperationBeforeUndoRedo,
      removeApplianceFromDrawingArea,
      shapesStack,
      undoIndex,
      updateDrawingShapes,
    ]
  );

  const hasBaselinePushed = useRef(false);

  const clearUndoStack = () => {
    setShapesStack([]);
    dispatch(clearUndoStackState());
    hasBaselinePushed.current = false;
  };

  // Push baseline state for loaded estimates once all required data is available.
  // This is a useEffect (not a direct call) because it must wait for the edge API
  // to resolve before pushing. This establishes the "floor" that undo can revert
  // to but not go past.
  useEffect(() => {
    if (
      !isNewEstimate &&
      !hasBaselinePushed.current &&
      areThereShapesInTheDrawingArea &&
      selectedEdge &&
      selectedColorId &&
      shapesStack.length === 0 &&
      undoIndex === undefined
    ) {
      const shapes = drawingTool.cloneShapes(drawingShapes);

      if (shapes.length > 1) {
        hasBaselinePushed.current = true;
        const index = 0;

        dispatch(setPreventRepricing(true));
        dispatch(updateUndoIndex(index));
        setShapesStack([shapes]);
        dispatch(
          pushOperationUndoStack({
            edge: selectedEdge,
            colorId: selectedColorId,
            activeDrawingObject: currentActiveDrawingObject,
            selectedCooktops,
            selectedRanges,
            selectedCustomCutouts,
            selectedAppliancesCutouts,
          })
        );
      }
    }
  }, [
    isNewEstimate,
    areThereShapesInTheDrawingArea,
    selectedEdge,
    selectedColorId,
    shapesStack.length,
    undoIndex,
    drawingShapes,
    dispatch,
    setShapesStack,
    currentActiveDrawingObject,
    selectedCooktops,
    selectedRanges,
    selectedCustomCutouts,
    selectedAppliancesCutouts,
  ]);

  useEffect(() => {
    if (!lastUserSelectedDesignOperation) {
      return;
    }

    if (isLatestOperationAnUndoRedoAction) {
      dispatch(
        updateLatestPushedUserDesignOperation({
          operation: lastUserSelectedDesignOperation,
          isUndoRedo: true,
        })
      );
    } else if (
      latestPushedUserDesignOperation !== lastUserSelectedDesignOperation
    ) {
      pushToUndoStack();
      dispatch(
        updateLatestPushedUserDesignOperation({
          operation: lastUserSelectedDesignOperation,
          isUndoRedo: false,
        })
      );
    }
  }, [
    areThereShapesInTheDrawingArea,
    drawingShapes,
    lastUserSelectedDesignOperation,
    latestPushedUserDesignOperation,
    selectedEdge,
    selectedColorId,
    isLatestOperationAnUndoRedoAction,
    dispatch,
    pushToUndoStack,
  ]);

  const stackContextValue: UndoStackContextType = {
    pushToUndoStack,
    pullFromUndoStack,
    clearUndoStack,
    shapesStack,
    undoIndex,
    maxUndoIndex,
  };

  return (
    <UndoStackContext.Provider value={stackContextValue}>
      {children}
    </UndoStackContext.Provider>
  );
};
